'use client';

import React, { useState, useEffect } from "react";
import { motion, type Variants } from "framer-motion";
import { FaInstagram, FaTelegramPlane, FaYoutube, FaArrowUp, FaEnvira } from "react-icons/fa";
import { useLocale } from "@/i18n/context";
import Link from "next/link";

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 100, damping: 20 },
  },
};

export function Footer() {
  const { t, isRtl } = useLocale();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const socials = [
    { 
      id: "instagram", 
      icon: FaInstagram, 
      url: "https://www.instagram.com/campaign_sabz?igsh=MWgycmttNHhqMG0zdA==", 
      label: "Instagram"
    },
    { 
      id: "telegram", 
      icon: FaTelegramPlane, 
      url: "https://t.me/campaign_sabz", 
      label: "Telegram"
    },
    { 
      id: "youtube", 
      icon: FaYoutube, 
      url: "https://youtube.com/@campaignsabz?si=e7DSa7_Aie20uaUr", 
      label: "YouTube"
    }
  ];

  const quickLinksList = [
    { name: t.nav.articles, href: "/articles" },
    { name: t.nav.volunteerForm, href: "/volunteer-form" },
    { name: t.nav.contactUs, href: "/contact-us" },
  ];

  return (
    <footer className="relative bg-[#206A4F] dark:bg-zinc-950 text-[#F5F0E8] dark:text-zinc-200 pt-20 pb-8 overflow-hidden selection:bg-[#F5F0E8]/20 dark:selection:bg-emerald-500/30 border-t border-transparent dark:border-zinc-800 transition-colors duration-300" dir={isRtl ? "rtl" : "ltr"}>
      
      {/* Background Glow Effect */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[1px] bg-gradient-to-r from-transparent via-[#F5F0E8]/20 dark:via-emerald-500/30 to-transparent" />
      <motion.div 
        animate={{ scale: [1, 1.1, 1], opacity: [0.08, 0.15, 0.08] }}
        transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
        className="absolute -top-[200px] -right-[200px] w-[500px] h-[500px] bg-[#F5F0E8] dark:bg-emerald-500 rounded-full blur-[140px] -z-0 pointer-events-none"
      />

      <motion.div 
        className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-8 mb-16">
          
          {/* Brand Intro */}
          <motion.div variants={itemVariants} className="md:col-span-5 flex flex-col items-start">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-[#F5F0E8]/10 dark:bg-emerald-500/15 flex items-center justify-center border border-[#F5F0E8]/20 dark:border-emerald-500/30 backdrop-blur-sm shadow-sm">
                <FaEnvira className="text-2xl text-[#F5F0E8] dark:text-emerald-400 animate-pulse" />
              </div>
              <div>
                <h2 className="text-3xl font-extrabold tracking-tight flex items-center gap-2 text-white dark:text-emerald-400">
                  {t.footer.brandName}
                  <span className="relative flex h-2.5 w-2.5 ms-1">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F5F0E8] dark:bg-emerald-400 opacity-60"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#F5F0E8] dark:bg-emerald-500"></span>
                  </span>
                </h2>
              </div>
            </div>
            
            <p className="text-[#F5F0E8]/80 dark:text-zinc-300 text-base leading-relaxed mb-6 font-medium max-w-md">
              {t.footer.description}
            </p>
            
            <div className="inline-flex items-center gap-2 bg-[#F5F0E8]/10 dark:bg-zinc-900 border border-[#F5F0E8]/20 dark:border-zinc-800 px-4 py-2 rounded-full text-sm font-bold text-[#F5F0E8] dark:text-emerald-300 shadow-sm">
            </div>
          </motion.div>
          
          {/* Quick Links */}
          <motion.div variants={itemVariants} className="md:col-span-3 flex flex-col">
            <h3 className="text-lg font-extrabold text-[#F5F0E8] dark:text-white mb-6 border-b border-[#F5F0E8]/10 dark:border-zinc-800 pb-2">
              {t.footer.quickLinks}
            </h3>
            <ul className="flex flex-col gap-3.5">
              {quickLinksList.map((item, idx) => (
                <li key={idx}>
                  <Link href={item.href} className="text-[#F5F0E8] dark:text-zinc-300 hover:opacity-80 dark:hover:text-emerald-400 transition-all duration-200 text-sm font-bold flex items-center gap-1.5 hover:translate-x-1.5 rtl:hover:-translate-x-1.5">
                    <span className="opacity-60">•</span>
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Social Links & Back to Top */}
          <motion.div variants={itemVariants} className="md:col-span-4 flex flex-col md:items-end">
            <h3 className="text-lg font-extrabold text-[#F5F0E8] dark:text-white mb-6">
              {t.footer.joinCommunity}
            </h3>
            
            <div className="flex flex-wrap gap-3 mb-8 justify-start md:justify-end">
              {socials.map((social) => (
                <motion.a 
                  key={social.id}
                  href={social.url} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[#F5F0E8]/10 dark:bg-zinc-900 border border-[#F5F0E8]/20 dark:border-zinc-800 text-[#F5F0E8] dark:text-zinc-200 hover:bg-[#F5F0E8] hover:text-[#206A4F] dark:hover:bg-emerald-500 dark:hover:text-black dark:hover:border-emerald-400 transition-all duration-300 font-bold shadow-sm"
                  aria-label={social.id}
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <social.icon className="text-lg" />
                  <span className="text-sm">
                    {social.label}
                  </span>
                </motion.a>
              ))}
            </div>

            <motion.button
              onClick={scrollToTop}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-[#F5F0E8]/15 dark:bg-emerald-500/20 hover:bg-[#F5F0E8]/25 dark:hover:bg-emerald-500/30 text-[#F5F0E8] dark:text-emerald-400 text-sm font-black transition-all border border-[#F5F0E8]/20 dark:border-emerald-500/30 shadow-md"
            >
              <span>{t.common.back}</span>
              <FaArrowUp size={12} />
            </motion.button>
          </motion.div>

        </div>
      </motion.div>

      {/* Copyright Line */}
      <motion.div 
        className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
      >
        <div className="pt-6 border-t border-[#F5F0E8]/15 dark:border-zinc-800/80 flex flex-col md:flex-row justify-between items-center gap-4 text-sm font-bold text-[#F5F0E8]/60 dark:text-zinc-500">
          <p>{t.footer.copyright}</p>
          
          <div className="flex items-center gap-1.5 text-[#F5F0E8]/80 dark:text-zinc-400">
            {t.footer.engineeredBy}
            <span className="font-extrabold text-[#F5F0E8] dark:text-emerald-400">
              {t.footer.samanic}
            </span>
          </div>
        </div>
      </motion.div>
    </footer>
  );
}